import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Phone, MessageCircle } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { useSite } from "@/context/SiteContext";
import { telHref, waHref } from "@/lib/api";

function StickyContact() {
  const { settings: s } = useSite();
  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 gap-px bg-black/50 shadow-[0_-4px_20px_rgba(0,0,0,0.25)] lg:hidden" data-testid="mobile-sticky-bar">
        <a href={telHref(s.phone)} className="flex items-center justify-center gap-2 bg-[color:var(--ink)] py-4 text-sm font-bold text-white" data-testid="sticky-call-btn">
          <Phone className="h-5 w-5 text-[color:var(--red)]" /> Hemen Ara
        </a>
        <a href={waHref(s)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-[#1f9d55] py-4 text-sm font-bold text-white" data-testid="sticky-whatsapp-btn">
          <MessageCircle className="h-5 w-5" /> WhatsApp
        </a>
      </div>
      <div className="fixed bottom-6 right-6 z-40 hidden flex-col gap-3 lg:flex">
        <a href={waHref(s)} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp ile iletişime geç" className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1f9d55] text-white shadow-xl transition-transform hover:scale-105" data-testid="float-whatsapp-btn">
          <MessageCircle className="h-6 w-6" />
        </a>
        <a href={telHref(s.phone)} aria-label="Hemen ara" className="flex h-14 w-14 items-center justify-center rounded-full bg-[color:var(--red)] text-white shadow-xl transition-transform hover:scale-105" data-testid="float-call-btn">
          <Phone className="h-6 w-6" />
        </a>
      </div>
    </>
  );
}

function loadGA(id) {
  if (!id || window.__gaLoaded) return;
  window.__gaLoaded = true;
  const sc = document.createElement("script");
  sc.async = true;
  sc.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(sc);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", id, { anonymize_ip: true });
}

function CookieBanner() {
  const { settings: s, ready } = useSite();
  const [consent, setConsent] = useState(() => localStorage.getItem("cookie_consent"));
  const [show, setShow] = useState(false);

  useEffect(() => {
    const open = () => setShow(true);
    window.addEventListener("open-cookie-prefs", open);
    return () => window.removeEventListener("open-cookie-prefs", open);
  }, []);
  useEffect(() => {
    if (ready && consent === "accepted") loadGA(s.ga_id);
  }, [ready, consent, s.ga_id]);
  useEffect(() => {
    if (!s.gsc_code) return;
    const m = document.createElement("meta");
    m.name = "google-site-verification";
    m.content = s.gsc_code;
    document.head.appendChild(m);
    return () => m.remove();
  }, [s.gsc_code]);

  const decide = (v) => {
    localStorage.setItem("cookie_consent", v);
    setConsent(v);
    setShow(false);
    window.dispatchEvent(new Event("cookie-consent-changed"));
  };
  if (consent && !show) return null;
  return (
    <div className="fixed inset-x-3 bottom-20 z-50 mx-auto max-w-xl rounded-lg border border-zinc-700 bg-[color:var(--slate)] p-5 text-sm text-zinc-300 shadow-2xl lg:bottom-6 lg:left-6 lg:right-auto" data-testid="cookie-banner">
      <p className="font-bold text-white">Çerez tercihleriniz</p>
      <p className="mt-2 leading-relaxed">
        Sitemizin çalışması için zorunlu çerezler kullanılır. Ziyaret istatistikleri için analitik çerezler ve gömülü harita yalnızca onay vermeniz hâlinde çalışır.{" "}
        <Link to="/cerez-politikasi" className="font-semibold text-white underline">Çerez Politikası</Link>
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => decide("accepted")} className="btn-red !py-2" data-testid="cookie-accept-btn">Kabul Et</button>
        <button onClick={() => decide("rejected")} className="btn-ghost-dark !py-2" data-testid="cookie-reject-btn">Yalnızca Zorunlu</button>
      </div>
    </div>
  );
}

export default function SiteLayout() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only focus:not-sr-only">İçeriğe geç</a>
      <Header />
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <StickyContact />
      <CookieBanner />
    </div>
  );
}
